import {
  computeGatepassExpiryDate,
  gatepassExpiryExceedsScValidity,
} from "@features/gatepass/schema/gatepassScValidity.utils";
import { t, type TFunction } from "i18next";
import { z } from "zod";

const required = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

const optional = (max: number, label = t("form-validation.field")) =>
  z.string().max(max, t("form-validation.max-length", { label, max })).optional();

export const optionalLettersWithSpaces = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .max(max, t("form-validation.max-length", { label, max }))
    .regex(/^[A-Za-z ]*$/, {
      message: t("form-validation.regex-only-letters", { label }),
    })
    .optional();

export const requiredLettersWithSpaces = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }))
    .regex(/^[A-Za-z ]+$/, {
      message: t("form-validation.regex-only-letters", { label }),
    });

const requiredPhone = (max: number, label = t("gatepass.phone-number")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .regex(/^\+[1-9]\d{6,14}$/, {
      message: t("form-validation.telephone", { label }),
    })
    .max(max, t("form-validation.max-length", { label, max }));

const requiredDate = (label: string) =>
  z.union([
    z.date({
      required_error: t("form-validation.required", { label }),
      invalid_type_error: t("form-validation.required", { label }),
    }),
    z.string().min(1, t("form-validation.required", { label })),
  ]);

const toStartOfDay = (value: Date): Date => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const parseDateValue = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export const passportNumberSchema = (
  max: number,
  label = t("security-clearance.passport-number"),
) =>
  z
    .preprocess((val) => (val === null ? "" : val), z.string())
    .superRefine((val, ctx) => {
      const normalized = val.trim();
      const min = 6;
      if (!normalized) return; // Only validate if value is present

      // Not alphanumeric
      if (!/^[a-zA-Z0-9]+$/.test(normalized)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.regex-no-special-chars", { label }),
        });
        return;
      }

      // Too short
      if (normalized.length < min) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.min-characters", { min }),
        });
        return;
      }

      if (normalized.length > Math.min(max, 10)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.max-length", { label, max: 10 }),
        });
      }
    })
    .optional();

export const emiratesIdNumberSchema = (label = t("security-clearance.emirates-id-no")) =>
  z
    .preprocess((val) => (val === null ? "" : val), z.string())
    .superRefine((val, ctx) => {
      if (!val) return; // Only validate if value is present
      if (val.length < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 18,
          type: "string",
          inclusive: true,
          message: t("form-validation.min-numbers", { label, min: 15 }),
        });
        return;
      }
      if (val.length > 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: 18,
          type: "string",
          inclusive: true,
          message: t("form-validation.max-length", { label, max: 15 }),
        });
        return;
      }
      if (!/^\d{3}-\d{4}-\d{7}-\d$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.invalid-emirates-id", { label }),
        });
      }
    })
    .optional();

const TRANSLATION_KEY = "gatepass";
export const getNormalGatepassFormSchema = (
  t: TFunction,
  options?: { isIndividualRole?: boolean },
) => {
  const isIndividualRole = options?.isIndividualRole === true;

  return z
    .object({
      SCStatus: z.string(),
      securityClearanceSerial: z.string().optional(),
      securityClearanceId: z.string().optional(),
      securityClearanceValidity: z.string().optional(),
      securityClearanceFott: z.string().optional(),
      gatepassCategory: z.string(),
      // Visitor Details
      passportNumber: passportNumberSchema(15, t(`${TRANSLATION_KEY}.passport-number`)),
      emiratesIdNumber: emiratesIdNumberSchema(t(`${TRANSLATION_KEY}.emirates-id-number`)),
      fullName: required(200, t(`${TRANSLATION_KEY}.fullName-en`)),
      nationality: required(200, t(`${TRANSLATION_KEY}.nationality`)),
      passportNumberExpirationDate: optional(
        20,
        t(`${TRANSLATION_KEY}.passport-number-expiration-date`),
      ),
      phoneNumber: requiredPhone(200, t(`${TRANSLATION_KEY}.phone-number`)),
      sponsorCompany: required(200, t(`${TRANSLATION_KEY}.sponsor-company`)),
      expectedEntryDate: requiredDate(t(`${TRANSLATION_KEY}.expected-entry-date`)),
      port: required(200, t(`${TRANSLATION_KEY}.port`)),
      gate: required(200, t(`${TRANSLATION_KEY}.gate-selection`)),
      visitorCompany: required(200, t(`${TRANSLATION_KEY}.visitor-company`)),
      visitorCompanyArabic: required(200, t(`${TRANSLATION_KEY}.visitor-company-name-ar`)),
      reference: optional(200, t(`${TRANSLATION_KEY}.reference`)),
      // Gatepass Details
      gatepassType: z.string().max(
        50,
        t("form-validation.max-length", {
          label: t(`${TRANSLATION_KEY}.type-of-gatepass`),
          max: 50,
        }),
      ),
      vip: z.boolean().nullable(),
      requestFor: z.string().max(
        20,
        t("form-validation.max-length", {
          label: t(`${TRANSLATION_KEY}.request-for`),
          max: 20,
        }),
      ),
      gatepassDuration: required(50, t(`${TRANSLATION_KEY}.gatepass-duration`)),
      permanentDuration: z.string().optional(),
      entryType: required(50, t(`${TRANSLATION_KEY}.entry-type`)),
      FOTTEntry: z.boolean().nullable(),
      isFottDisabled: z.boolean(),
      // Vehicle Details
      registrationEmirate: optional(200, t(`${TRANSLATION_KEY}.registration-emirate`)),
      vehicleType: optional(200, t(`${TRANSLATION_KEY}.vehicle-type`)),
      vehicleColour: optionalLettersWithSpaces(200, t(`${TRANSLATION_KEY}.vehicle-colour`)),
      category: optional(200, t(`${TRANSLATION_KEY}.category`)),
      plateNumber: optional(50, t(`${TRANSLATION_KEY}.plate-number`)),
      // Locations
      locations: z.array(
        z.object({
          location: required(200, t(`${TRANSLATION_KEY}.primary-location`)),
          entryPurpose: z
            .string()
            .trim()
            .min(1, t("form-validation.required", { label: t(`${TRANSLATION_KEY}.entry-purpose`) }))
            .max(
              500,
              t("form-validation.max-length", {
                label: t(`${TRANSLATION_KEY}.entry-purpose`),
                max: 500,
              }),
            ),
        }),
      ),
      groupMembers: z.array(
        z
          .object({
            passportNumber: passportNumberSchema(15, t(`${TRANSLATION_KEY}.passport-number`)),
            emiratesIdNumber: emiratesIdNumberSchema(t(`${TRANSLATION_KEY}.emirates-id-number`)),
            fullName: optional(200, t(`${TRANSLATION_KEY}.fullName-en`)),
            nationality: optional(200, t(`${TRANSLATION_KEY}.nationality`)),
            passportExpiryDate: optional(
              20,
              t(`${TRANSLATION_KEY}.passport-number-expiration-date`),
            ),
            phoneNumber: optional(20, t(`${TRANSLATION_KEY}.phone-number`)),
            securityClearanceId: z.string().optional(),
            securityClearanceStatus: z.string().optional(),
            securityClearanceSerial: z.string().optional(),
            securityClearanceValidity: z.string().optional(),
            securityClearanceFott: z.string().optional(),
          })
          .superRefine((member, memberCtx) => {
            const hasPassportNumber = Boolean(member.passportNumber?.trim());
            const hasEmiratesIdNumber = Boolean(member.emiratesIdNumber?.trim());

            if (!hasPassportNumber && !hasEmiratesIdNumber) {
              memberCtx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.required", {
                  label: t(`${TRANSLATION_KEY}.passport-number`),
                }),
                path: ["passportNumber"],
              });

              memberCtx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.required", {
                  label: t(`${TRANSLATION_KEY}.emirates-id-number`),
                }),
                path: ["emiratesIdNumber"],
              });
            }

            if ((hasPassportNumber || hasEmiratesIdNumber) && !member.securityClearanceId?.trim()) {
              memberCtx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t(`${TRANSLATION_KEY}.notifications.security-clearance-not-found`),
                path: ["securityClearanceId"],
              });
            }
          }),
      ),
      // Photo
      photoUrl: optional(200, t(`${TRANSLATION_KEY}.photoUrl`)),
    })
    .superRefine((data, ctx) => {
      const duplicateScMessage = t(`${TRANSLATION_KEY}.notifications.group-member-already-added`);
      const hasPassportNumber = Boolean(data.passportNumber?.trim());
      const hasEmiratesIdNumber = Boolean(data.emiratesIdNumber?.trim());

      if (!hasPassportNumber && !hasEmiratesIdNumber) {
        const passportRequiredMessage = t("form-validation.required", {
          label: t(`${TRANSLATION_KEY}.passport-number`),
        });
        const emiratesRequiredMessage = t("form-validation.required", {
          label: t(`${TRANSLATION_KEY}.emirates-id-number`),
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: passportRequiredMessage,
          path: ["passportNumber"],
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: emiratesRequiredMessage,
          path: ["emiratesIdNumber"],
        });
      }

      if ((hasPassportNumber || hasEmiratesIdNumber) && !data.securityClearanceId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(`${TRANSLATION_KEY}.notifications.security-clearance-not-found`),
          path: ["securityClearanceId"],
        });
      }

      const visitorSecurityClearanceId = String(data.securityClearanceId ?? "").trim();
      if (visitorSecurityClearanceId) {
        const duplicateGroupMemberIndexes: number[] = [];
        data.groupMembers.forEach((groupMember, index) => {
          if (String(groupMember.securityClearanceId ?? "").trim() === visitorSecurityClearanceId) {
            duplicateGroupMemberIndexes.push(index);
          }
        });

        if (duplicateGroupMemberIndexes.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: duplicateScMessage,
            path: ["securityClearanceId"],
          });

          duplicateGroupMemberIndexes.forEach((index) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: duplicateScMessage,
              path: ["groupMembers", index, "securityClearanceId"],
            });
          });
        }
      }

      const firstGroupMemberIndexByScId = new Map<string, number>();
      data.groupMembers.forEach((groupMember, index) => {
        const groupMemberSecurityClearanceId = String(groupMember.securityClearanceId ?? "").trim();
        if (!groupMemberSecurityClearanceId) {
          return;
        }

        const firstIndex = firstGroupMemberIndexByScId.get(groupMemberSecurityClearanceId);
        if (firstIndex === undefined) {
          firstGroupMemberIndexByScId.set(groupMemberSecurityClearanceId, index);
          return;
        }

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: duplicateScMessage,
          path: ["groupMembers", firstIndex, "securityClearanceId"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: duplicateScMessage,
          path: ["groupMembers", index, "securityClearanceId"],
        });
      });

      const seen = new Map<string, number>();

      data.locations.forEach((row, index) => {
        const rawValue = row.location?.trim();
        if (!rawValue) {
          return;
        }

        const key = rawValue.toLowerCase();
        if (seen.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(`${TRANSLATION_KEY}.duplicate-location`),
            path: ["locations", index, "location"],
          });
          return;
        }

        seen.set(key, index);
      });

      if (data.gatepassType?.trim().toLowerCase() === "group" && data.groupMembers.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", { label: t(`${TRANSLATION_KEY}.group-members`) }),
          path: ["groupMembers"],
        });
      }

      if (!isIndividualRole && !data.gatepassType?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.type-of-gatepass`),
          }),
          path: ["gatepassType"],
        });
      }

      if (!isIndividualRole && (data.vip === null || data.vip === undefined)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.vip`),
          }),
          path: ["vip"],
        });
      }

      if (!isIndividualRole && !data.requestFor?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.request-for`),
          }),
          path: ["requestFor"],
        });
      }

      if (data.FOTTEntry === null || data.FOTTEntry === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.fott-entry`),
          }),
          path: ["FOTTEntry"],
        });
      }

      if (
        !isIndividualRole &&
        data.gatepassDuration?.trim().toLowerCase() === "permanent" &&
        !data.permanentDuration?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.security-clearance-duration`),
          }),
          path: ["permanentDuration"],
        });
      }

      if (data.vehicleType?.trim() && !data.registrationEmirate?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.registration-emirate`),
          }),
          path: ["registrationEmirate"],
        });
      }

      if (data.vehicleType?.trim() && !data.plateNumber?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.vehicle-plate-number`),
          }),
          path: ["plateNumber"],
        });
      }

      const parsedExpectedEntryDate = parseDateValue(data.expectedEntryDate);
      if (parsedExpectedEntryDate) {
        const selectedDate = toStartOfDay(parsedExpectedEntryDate);
        const today = toStartOfDay(new Date());

        if (selectedDate.getTime() < today.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-not-be-in-past", {
              label: t(`${TRANSLATION_KEY}.expected-entry-date`),
            }),
            path: ["expectedEntryDate"],
          });
        }
      }

      const gatepassExpiryDate = computeGatepassExpiryDate(
        data.expectedEntryDate,
        data.gatepassDuration,
        data.permanentDuration,
      );

      if (gatepassExpiryDate) {
        const exceedsMsg = t(`${TRANSLATION_KEY}.gatepass-exceeds-security-clearance-expiry`);
        if (gatepassExpiryExceedsScValidity(gatepassExpiryDate, data.securityClearanceValidity)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: exceedsMsg,
            path: ["securityClearanceValidity"],
          });
        }
        if (data.gatepassType?.trim().toLowerCase() === "group") {
          data.groupMembers.forEach((member, index) => {
            if (
              gatepassExpiryExceedsScValidity(gatepassExpiryDate, member.securityClearanceValidity)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: exceedsMsg,
                path: ["groupMembers", index, "securityClearanceValidity"],
              });
            }
          });
        }
      }
    });
};

export type NormalGatepassFormData = z.infer<ReturnType<typeof getNormalGatepassFormSchema>>;
